import { format, parse, addDays, differenceInDays, isBefore, isAfter, isSameDay } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export class DateTimeUtil {
    static DEFAULT_DATE_FORMAT = "dd/MM/yyyy";
    static DEFAULT_DATETIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

    static DATETIME_FANCY_1 = "MMM D, YYYY hh:mm";
    static DATETIME_FANCY_2 = "MMM D, hh:mm a";

    // @vishwesh5544 v2 supports date-fns v3, in date-fns v3 'D' is deprecated and 'd' is used for day of the month
    static DATETIME_FANCY_2_v2 = "MMM d, hh:mm a";
    
    static MONTH_DATE = "MMM d";
    static DAY_OF_WEEK = "ddd";
    static TIME_FORMAT_1 = "HH:mm a";
    // Formatting dates using date-fns
    static formatDate(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return format(new Date(date), dateFormat);
    }

    // Add this method inside DateTimeUtil class
    static formatShortDate(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("ddd, D MMM"); // Example output: "Mon, 14 Feb"
    }

    // Parsing dates using date-fns
    static parseDate(
        dateString: string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): Date {
        if (!dateString) return new Date(NaN);
        return parse(dateString, dateFormat, new Date());
    }

    // Adding days using date-fns
    static addDaysToDate(date: Date | string | null | undefined, days: number): Date {
        if (!date) return new Date(NaN);
        return addDays(new Date(date), days);
    }

    // Difference in days using date-fns
    static differenceInDays(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return differenceInDays(new Date(dateLeft), new Date(dateRight));
    }

    static isSameDayWithDateFns(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return isSameDay(new Date(dateLeft), new Date(dateRight));
    }

    // Check if two dates are the same day using dayjs
    static isSameDayWithDayjs(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return dayjs.utc(dateLeft).isSame(dayjs.utc(dateRight), "day");
    }

    // Check if a date is before another date using date-fns
    static isBefore(date: Date | string | null | undefined, dateToCompare: Date | string | null | undefined): boolean {
        if (!date || !dateToCompare) return false;
        return isBefore(new Date(date), new Date(dateToCompare));
    }

    // Check if a date is after another date using date-fns
    static isAfter(date: Date | string | null | undefined, dateToCompare: Date | string | null | undefined): boolean {
        if (!date || !dateToCompare) return false;
        return isAfter(new Date(date), new Date(dateToCompare));
    }

    // Formatting dates using dayjs
    static formatWithDayjs(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return dayjs(date).format(dateFormat);
    }

    // Parsing dates using dayjs
    static parseWithDayjs(dateString: string | null | undefined): dayjs.Dayjs {
        return dateString ? dayjs(dateString) : dayjs(null);
    }

    // Adding days using dayjs
    static addDaysWithDayjs(date: Date | string | null | undefined, days: number): dayjs.Dayjs {
        return date ? dayjs(date).add(days, "day") : dayjs(null);
    }

    // Difference in days using dayjs
    static differenceInDaysWithDayjs(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return dayjs(dateLeft).diff(dayjs(dateRight), "day");
    }

    // Check if a date is before another date using dayjs
    static isBeforeWithDayjs(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs(date).isBefore(dayjs(dateToCompare));
    }

    // Check if a date is after another date using dayjs
    static isAfterWithDayjs(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isAfter(dayjs.utc(dateToCompare));
    }

    // Format date/time range
    static formatDateTimeRange(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid date",
        includeDate: boolean = true
    ): string {
        const start = dayjs(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        let formattedStart;
        if (includeDate) {
            formattedStart = start.isValid() ? start.format("MMM D h:mmA") : invalidMessage;
        } else {
            formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        }

        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatDateTimeRangeUtc(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid date",
        includeDate: boolean = true
    ): string {
        const start = dayjs.utc(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs.utc(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        let formattedStart;
        if (includeDate) {
            formattedStart = start.isValid() ? start.format("MMM D, h:mmA") : invalidMessage;
        } else {
            formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        }

        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatTimeRangeUtc(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid time"
    ): string {
        const start = dayjs.utc(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs.utc(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        const formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatDateRangeFancy(start: string, end: string, useUTC: boolean = true): string {
        let startDate: dayjs.Dayjs | Date;
        let endDate: dayjs.Dayjs | Date;

        // Try parsing with dayjs first
        if (dayjs(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT, true).isValid()) {
            startDate = useUTC
                ? dayjs.utc(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT)
                : dayjs(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
            endDate = useUTC
                ? dayjs.utc(end, DateTimeUtil.DEFAULT_DATETIME_FORMAT)
                : dayjs(end, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        } else {
            // Fallback to native Date parsing
            startDate = new Date(start);
            endDate = new Date(end);
        }

        // Ensure both dates are valid
        if (
            (startDate instanceof Date && isNaN(startDate.getTime())) ||
            (endDate instanceof Date && isNaN(endDate.getTime()))
        ) {
            return "Invalid date range";
        }

        // Helper function to format correctly based on instance type
        const formatDate = (date: dayjs.Dayjs | Date, formatStr: string): string => {
            if (dayjs.isDayjs(date)) {
                return date.format(formatStr);
            } else {
                return format(date as Date, formatStr);
            }
        };

        const startMonth = formatDate(startDate, "MMM");
        const endMonth = formatDate(endDate, "MMM");

        const startDay = formatDate(startDate, "D");
        const endDay = formatDate(endDate, "D");

        if (startDay === endDay && startMonth === endMonth) {
            return `${startMonth} ${startDay}`;
        }

        return startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }

    // Calculate duration between two dates
    static getDuration(start?: string | null, end?: string | null): string {
        if (!start || !end) return "0h 0m";

        const normalizeTimeString = (value: string): string => {
            if (!value) return value;
            if (value.includes("T") || value.includes(" ")) {
                return value;
            }
            return `2000-01-01 ${value}`;
        };

        const startTime = dayjs(normalizeTimeString(start));
        const endTime = dayjs(normalizeTimeString(end));

        if (!startTime.isValid() || !endTime.isValid()) return "0h 0m";

        let durationMinutes = endTime.diff(startTime, "minute");

        if (durationMinutes < 0) {
            durationMinutes += 24 * 60;
        }

        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;
        return `${hours}h ${minutes}m`;
    }

    // Get formatted date string like "Aug 2"
    static getFormattedDate(dateString: string | null | undefined, invalidMessage: string = "Invalid date"): string {
        if (!dateString) return invalidMessage;
        const date = dayjs(dateString, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        return date.isValid() ? date.format("MMM D") : invalidMessage;
    }

    static getFormattedTime(timeString: string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!timeString) return invalidMessage;
        const time = dayjs(timeString, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        return time.isValid() ? time.format("h:mmA") : invalidMessage;
    }

    static getFormattedTimeUtc(timeString: string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!timeString) return invalidMessage;
        const time = dayjs(timeString, DateTimeUtil.DEFAULT_DATETIME_FORMAT).utc();
        return time.isValid() ? time.format("h:mmA") : invalidMessage;
    }

    static formatDateToMySQL(date: Date, excludeTime: boolean = false): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const seconds = date.getSeconds().toString().padStart(2, "0");

        if (excludeTime) {
            return `${year}-${month}-${day}`;
        }

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    static formatDayjsToMySQL(date: dayjs.Dayjs): string {
        return date.format("YYYY-MM-DD HH:mm:ss");
    }

    static convertUtcToLocalTime(utcTimeString: string | null | undefined): string {
        if (!utcTimeString) return "Invalid time";
        
        // Parse the UTC time string (MySQL format: YYYY-MM-DD HH:mm:ss)
        const utcTime = dayjs.utc(utcTimeString, "YYYY-MM-DD HH:mm:ss");
        
        if (!utcTime.isValid()) return "Invalid time";
        
        // Convert to local browser time and format back to MySQL format
        return utcTime.local().format("YYYY-MM-DD HH:mm:ss");
    }

    /**
     * Display datetime in specified format with timezone conversion
     * @param datetime - MySQL datetime string (YYYY-MM-DD HH:mm:ss)
     * @param format - PHP-style date format (e.g., "M d, Y h:i A", "Y-m-d H:i:s")
     * @param timezone - "local" for browser local time, or numeric offset like "-5" for EST, "5.5" for IST
     * @param isUtc - Whether the input datetime is UTC (default: true for database columns like "when")
     * @returns Formatted datetime string
     */
    static DisplayDateTime(
        datetime: string | null | undefined,
        format: string = "M d, Y h:i A",
        timezone: "local" | string = "local",
        isUtc: boolean = true
    ): string {
        if (!datetime) return "Invalid datetime";
        
        // Parse the datetime string
        let parsedDateTime: dayjs.Dayjs;
        
        if (isUtc) {
            // Parse as UTC datetime (common for database "when" columns)
            parsedDateTime = dayjs.utc(datetime, "YYYY-MM-DD HH:mm:ss");
        } else {
            // Parse as local datetime (common for transaction data like punch datetime)
            parsedDateTime = dayjs(datetime, "YYYY-MM-DD HH:mm:ss");
        }
        
        if (!parsedDateTime.isValid()) return "Invalid datetime";
        
        // Convert timezone based on parameter
        let targetDateTime: dayjs.Dayjs;
        
        if (timezone === "local") {
            // Convert to browser's local timezone
            targetDateTime = isUtc ? parsedDateTime.local() : parsedDateTime;
        } else {
            // Convert to specific timezone offset
            const offset = parseFloat(timezone);
            if (isNaN(offset)) return "Invalid timezone";
            
            if (isUtc) {
                // If input is UTC, apply the offset
                targetDateTime = parsedDateTime.utcOffset(offset * 60);
            } else {
                // If input is already local, first convert to UTC then apply offset
                targetDateTime = parsedDateTime.utc().utcOffset(offset * 60);
            }
        }
        
        // Convert PHP-style format to dayjs format
        const dayjsFormat = DateTimeUtil.convertPhpFormatToDayjs(format);
        
        return targetDateTime.format(dayjsFormat);
    }

    /**
     * Convert PHP date format to dayjs format
     * @param phpFormat - PHP-style date format string
     * @returns dayjs-compatible format string
     */
    static convertPhpFormatToDayjs(phpFormat: string): string {
        return phpFormat
            // Date formats - order matters, do specific ones first
            .replace(/F/g, "MMMM")      // Full month name
            .replace(/M/g, "MMM")       // Abbreviated month name (MUST come after F)
            .replace(/Y/g, "YYYY")      // 4-digit year
            .replace(/y/g, "YY")        // 2-digit year
            .replace(/m/g, "MM")        // Month with leading zeros
            .replace(/n/g, "M")         // Month without leading zeros
            .replace(/D/g, "ddd")       // Abbreviated day name (MUST come before d)
            .replace(/l/g, "dddd")      // Full day name
            .replace(/d/g, "DD")        // Day with leading zeros (MUST come after D)
            .replace(/j/g, "D")         // Day without leading zeros
            .replace(/N/g, "E")         // ISO-8601 day of week
            .replace(/S/g, "Do")        // Ordinal suffix for day
            .replace(/z/g, "DDD")       // Day of year
            .replace(/W/g, "WW")        // ISO-8601 week number
            .replace(/t/g, "")          // Days in month (not supported in dayjs)
            
            // Time formats
            .replace(/H/g, "HH")        // 24-hour format with leading zeros
            .replace(/G/g, "H")         // 24-hour format without leading zeros
            .replace(/h/g, "hh")        // 12-hour format with leading zeros
            .replace(/g/g, "h")         // 12-hour format without leading zeros
            .replace(/i/g, "mm")        // Minutes with leading zeros
            .replace(/s/g, "ss")        // Seconds with leading zeros
            .replace(/u/g, "SSS")       // Microseconds (milliseconds in dayjs)
            .replace(/a/g, "a")         // Lowercase am/pm
            .replace(/A/g, "A")         // Uppercase AM/PM
            .replace(/B/g, "")          // Swatch internet time (not supported)
            .replace(/I/g, "")          // Daylight saving time (not supported)
            .replace(/O/g, "ZZ")        // Difference to GMT
            .replace(/P/g, "Z")         // Difference to GMT with colon
            .replace(/T/g, "")          // Timezone abbreviation (not supported)
            .replace(/Z/g, "")          // Timezone offset in seconds (not supported)
            .replace(/c/g, "YYYY-MM-DDTHH:mm:ssZ") // ISO 8601
            .replace(/r/g, "ddd, DD MMM YYYY HH:mm:ss Z") // RFC 2822
            .replace(/U/g, "X");        // Seconds since Unix epoch
    }

    /***
     * Dayjs specific
     */

    // Format a date using dayjs in UTC
    static FormatWithDayjsUtc(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return dayjs.utc(date).format(dateFormat);
    }

    static FormatDurationFromStart(
        start: Date | string | null | undefined
    ): string {
        if (!start) return "Invalid start time";

        const now = dayjs.utc();
        const startTime = dayjs.utc(start);
        const diff = now.diff(startTime);

        const dur = dayjs.duration(diff);
        const hours = Math.floor(dur.asHours());
        const minutes = dur.minutes();

        const hoursStr = hours > 0 ? `${hours} hr${hours !== 1 ? "s" : ""}` : "";
        const minutesStr = `${minutes} m`;

        return `${hoursStr}${hoursStr && minutes ? " " : ""}${minutesStr}`;
    }

    // Parse a date using dayjs in UTC
    static ParseWithDayjsUtc(dateString: string | null | undefined): dayjs.Dayjs {
        return dateString ? dayjs.utc(dateString) : dayjs(null);
    }

    // Add days to a date using dayjs in UTC
    static AddDaysWithDayjsUtc(date: Date | string | null | undefined, days: number): dayjs.Dayjs {
        return date ? dayjs.utc(date).add(days, "day") : dayjs(null);
    }

    // Difference in days using dayjs in UTC
    static DifferenceInDaysWithDayjsUtc(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return dayjs.utc(dateLeft).diff(dayjs.utc(dateRight), "day");
    }

    // Check if a date is before another date using dayjs in UTC
    static IsBeforeWithDayjsUtc(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isBefore(dayjs.utc(dateToCompare));
    }

    // Check if a date is after another date using dayjs in UTC
    static IsAfterWithDayjsUtc(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isAfter(dayjs.utc(dateToCompare));
    }

    // Check if two dates are the same day using dayjs in UTC
    static IsSameDayWithDayjsUtc(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return dayjs.utc(dateLeft).isSame(dayjs.utc(dateRight), "day");
    }

    /**
     * Generates an array of dates between a specified start and end date.
     * Optionally includes or excludes the start and end dates and can ignore the time component.
     *
     * @param start - The start date of the range.
     * @param end - The end date of the range.
     * @param includeBounds - Boolean flag to include or exclude the start and end dates in the result. Defaults to true (includes bounds).
     * @param ignoreTime - Boolean flag to reset the time of each date to midnight (00:00:00). Defaults to true.
     * @returns Array of Date objects representing each day in the range.
     */
    static GetDatesBetween(start: Date, end: Date, includeBounds: boolean = true, ignoreTime: boolean = true): Date[] {
        const dates = []; // Array to hold each date in the range
        const currentDate = new Date(start); // Copy of start date to iterate through the range

        // Reset time to midnight if ignoreTime is true
        if (ignoreTime) {
            currentDate.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
        }

        // Adjust starting point if bounds are not included
        if (!includeBounds) {
            currentDate.setDate(currentDate.getDate() + 1); // Move to the day after the start date
        }

        // Loop through dates until reaching or including the end date based on includeBounds
        while (currentDate <= end) {
            dates.push(new Date(currentDate)); // Add the current date to the array
            currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
        }

        // If bounds are excluded, ensure the end date itself is not included in the result
        if (!includeBounds && dates[dates.length - 1].getTime() === end.getTime()) {
            dates.pop(); // Remove the end date if it was included
        }

        return dates; // Return the array of dates within the specified range
    }

    // Returns the day name from a given date (e.g., Monday, Tuesday)
    static getDayName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd");
    }

    // Returns the abbreviated day name from a given date (e.g., Mon, Tue)
    static getAbbreviatedDayName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("ddd");
    }

    // Returns the full month name from a given date (e.g., January, February)
    static getMonthName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("MMMM");
    }

    // Formats a date in a readable "Day, Date Month Year" format (e.g., Monday, 15 January 2023)
    static formatFullDate(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd, D MMMM YYYY");
    }

    // Combines day and time into a readable format (e.g., "Monday 2:30 PM")
    static formatDayAndTime(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd h:mm A");
    }

    // Get time in 24-hour format (e.g., 14:30)
    static getTime24HourFormat(date: Date | string | null | undefined): string {
        if (!date) return "Invalid time";
        return dayjs(date).format("HH:mm");
    }

    static formatDateRange = (start: string, end: string): string => {
        const startDate = new Date(start);
        const endDate = new Date(end);

        const startMonth = format(startDate, "MMM");
        const endMonth = format(endDate, "MMM");

        const startDay = format(startDate, "d");
        const endDay = format(endDate, "d");

        if (startDay === endDay) {
            return `${startMonth} ${startDay}`;
        }

        return startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    };

    static formatElegantTime(time: Date | string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!time) return invalidMessage;
        const parsed = dayjs.utc(time);
        return parsed.isValid() ? parsed.format("hh:mm A") : invalidMessage;
    }

    static formatElegantTimeRange(
        start: string | null | undefined,
        end: string | null | undefined,
        invalidMessage: string = "N/A"
    ): string {
        if (!start || !end) return invalidMessage;

        const startTime = dayjs.utc(start);
        const endTime = dayjs.utc(end);

        const isStartMidnight = startTime.format("HH:mm:ss") === "00:00:00";
        const isEndMidnight = endTime.format("HH:mm:ss") === "00:00:00";

        // If both are exactly midnight, consider it a full-day leave
        if (isStartMidnight && isEndMidnight) {
            return "Full day";
        }

        if (!startTime.isValid() || !endTime.isValid()) {
            return invalidMessage;
        }

        return `${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}`;
    }

    /**
     * Detects browser's locale preference for 12-hour vs 24-hour time format
     * @returns true if browser prefers 12-hour format, false if 24-hour format
     */
    static isBrowser12HourFormat(): boolean {
        try {
            // Create a test date and format it without specifying hour12
            // This will use the browser's default locale preference
            const testDate = new Date(2020, 0, 1, 13, 0, 0); // 1 PM
            
            // Use Intl.DateTimeFormat without hour12 to get browser's default preference
            const formatter = new Intl.DateTimeFormat(navigator.language, {
                hour: 'numeric'
            });
            const parts = formatter.formatToParts(testDate);
            
            // Check if the formatted parts include a dayPeriod (AM/PM) indicator
            // If it does, the browser prefers 12-hour format
            const hasAmPm = parts.some(part => part.type === 'dayPeriod');
            
            // Also check the formatted string itself - some locales might format differently
            const formattedString = formatter.format(testDate);
            const hasAmPmInString = /am|pm/i.test(formattedString);
            
            // Return true if browser prefers 12-hour format (has AM/PM indicators)
            return hasAmPm || hasAmPmInString;
        } catch (error) {
            console.error('[isBrowser12HourFormat] Error:', error);
            // Default to 12-hour format if detection fails
            return true;
        }
    }

    /**
     * Pretty prints a time string (HH:mm:ss or HH:mm) based on browser's locale preference
     * @param timeString - Time string in format "HH:mm:ss" or "HH:mm"
     * @param invalidMessage - Message to return if time is invalid
     * @returns Formatted time string (e.g., "12AM", "11:59PM", or "00:00", "23:59")
     */
    static prettyPrintTime(timeString: string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!timeString || typeof timeString !== 'string') return invalidMessage;

        const trimmed = timeString.trim();
        if (!trimmed) return invalidMessage;

        // Remove seconds if present (HH:mm:ss -> HH:mm)
        const timeOnly = trimmed.split(':').slice(0, 2).join(':');
        const parts = timeOnly.split(':');
        
        if (parts.length < 2) return invalidMessage;

        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);

        if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
            return invalidMessage;
        }

        const is12Hour = DateTimeUtil.isBrowser12HourFormat();

        if (is12Hour) {
            // 12-hour format
            let displayHours = hours;
            let period = 'AM';

            if (hours === 0) {
                displayHours = 12; // Midnight
            } else if (hours === 12) {
                period = 'PM'; // Noon
            } else if (hours > 12) {
                displayHours = hours - 12;
                period = 'PM';
            }

            // Format: "12AM", "1:30PM", "11:59PM"
            if (minutes === 0) {
                return `${displayHours}${period}`;
            } else {
                return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
            }
        } else {
            // 24-hour format
            return timeOnly;
        }
    }

    /**
     * Pretty prints a time range (start - end) based on browser's locale preference
     * @param startTime - Start time string in format "HH:mm:ss" or "HH:mm"
     * @param endTime - End time string in format "HH:mm:ss" or "HH:mm"
     * @param invalidMessage - Message to return if times are invalid
     * @returns Formatted time range string (e.g., "12AM-11:59PM" or "00:00-23:59")
     */
    static prettyPrintTimeRange(
        startTime: string | null | undefined,
        endTime: string | null | undefined,
        invalidMessage: string = "N/A"
    ): string {
        if (!startTime || !endTime || typeof startTime !== 'string' || typeof endTime !== 'string') {
            return invalidMessage;
        }

        const formattedStart = DateTimeUtil.prettyPrintTime(startTime, invalidMessage);
        const formattedEnd = DateTimeUtil.prettyPrintTime(endTime, invalidMessage);

        if (formattedStart === invalidMessage || formattedEnd === invalidMessage) {
            return invalidMessage;
        }

        return `${formattedStart}-${formattedEnd}`;
    }
}

export default DateTimeUtil;
